import { useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { getApprovalDisplay } from "@/components/approval-display";
import { Check, Pencil, X, Utensils, Dumbbell, ClipboardCheck, Brain } from "lucide-react";
import type { ApprovalContinuationStatus } from "@/screens/approval-recovery";

export interface ApprovalCardProps {
  approvalId: string;
  toolName: string;
  args: unknown;
  continuationStatus?: ApprovalContinuationStatus;
  retryLabel?: string;
  retryEdit?: boolean;
  onApprove: (reason?: string) => Promise<boolean>;
  onReject: (reason?: string) => Promise<boolean>;
  onEdit: (template: string) => Promise<boolean>;
}

function getToolMeta(toolName: string) {
  if (toolName.includes("logMeal")) {
    return {
      icon: Utensils,
      title: "Log meal",
    };
  }
  if (toolName.includes("logExercise")) {
    return {
      icon: Dumbbell,
      title: "Log exercise",
    };
  }
  if (toolName.includes("updateFoodMemory")) {
    return {
      icon: Brain,
      title: "Update food memory",
    };
  }
  if (toolName.includes("deleteFoodMemory")) {
    return {
      icon: Brain,
      title: "Forget food memory",
    };
  }
  return {
    icon: ClipboardCheck,
    title: "Confirm action",
  };
}

export function ApprovalCard({
  approvalId,
  toolName,
  args,
  continuationStatus,
  retryLabel,
  retryEdit = false,
  onApprove,
  onReject,
  onEdit,
}: ApprovalCardProps) {
  const [pendingAction, setPendingAction] = useState<"approved" | "rejected" | "edited" | null>(null);
  const { icon: Icon } = getToolMeta(toolName);
  const display = getApprovalDisplay(toolName, args);
  const { title, description } = display;
  const kcal = display.totalCalories ?? display.currentCalories;
  const isBusy = pendingAction !== null;
  const isRetryingContinuation = continuationStatus === "retry";
  const isRetryingDecision = continuationStatus === "retry-decision";
  const isContinuing = continuationStatus === "continuing";
  const isRecording = continuationStatus === "recording";
  const isRecorded = continuationStatus === "recorded";
  const decisionsDisabled =
    isBusy ||
    (continuationStatus !== undefined && continuationStatus !== "retry-decision");
  const approvalDisabled = isBusy || isRecording || isRecorded || isContinuing;

  const handleApprove = async () => {
    if (approvalDisabled || !display.canApprove) return;
    setPendingAction("approved");
    if (retryEdit) {
      await onEdit(display.editTemplate);
    } else {
      await onApprove();
    }
    setPendingAction(null);
  };

  const handleReject = async () => {
    if (decisionsDisabled) return;
    setPendingAction("rejected");
    await onReject();
    setPendingAction(null);
  };

  const handleEdit = async () => {
    if (decisionsDisabled || !display.canEdit) return;
    setPendingAction("edited");
    await onEdit(display.editTemplate);
    setPendingAction(null);
  };

  return (
    <Card
      className={cn(
        "max-w-[90%] border-l-4 border-l-accent p-4",
        continuationStatus !== undefined && "border-l-accent/70",
      )}
      data-testid={`approval-card-${approvalId}`}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-accent-light p-2 text-accent">
          <Icon size={18} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text">
            {title}: {description}
          </div>
          {kcal !== undefined && (
            <div className="mt-2 rounded-xl bg-accent-light px-3 py-2 text-accent">
              <div className="text-xs font-semibold uppercase tracking-wide">
                {display.calorieLabel ?? "Calories"}
              </div>
              <div className="text-xl font-bold leading-tight">
                {kcal.toLocaleString()} kcal
              </div>
            </div>
          )}
          {display.changedFields.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {display.changedFields.map((field) => (
                <span
                  key={field}
                  className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-text-muted"
                >
                  {field}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
          {!display.canApprove && !isRetryingContinuation && (
            <p className="basis-full rounded-lg bg-warning/10 px-3 py-2 text-xs font-medium text-warning">
              Approval details are unavailable. Reject this request and ask BiteTrack to try again.
            </p>
          )}
          {isRecording && (
            <p className="basis-full rounded-lg bg-accent-light px-3 py-2 text-xs font-medium text-accent">
              Recording your decision...
            </p>
          )}
          {isRetryingDecision && (
            <p className="basis-full rounded-lg bg-warning/10 px-3 py-2 text-xs font-medium text-warning">
              BiteTrack could not record the decision. Retry the same decision.
            </p>
          )}
          {isRecorded && (
            <p className="basis-full rounded-lg bg-accent-light px-3 py-2 text-xs font-medium text-accent">
              Decision recorded. Complete the remaining approvals to continue.
            </p>
          )}
          {isRetryingContinuation && (
            <p className="basis-full rounded-lg bg-warning/10 px-3 py-2 text-xs font-medium text-warning">
              The approval was recorded, but BiteTrack could not continue. Retry the continuation.
            </p>
          )}
          {isContinuing && (
            <p className="basis-full rounded-lg bg-accent-light px-3 py-2 text-xs font-medium text-accent">
              Decision recorded. BiteTrack is continuing...
            </p>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleApprove()}
            loading={pendingAction === "approved"}
            disabled={approvalDisabled || !display.canApprove}
            data-testid="approve-btn"
            className="min-h-11 flex-1"
          >
            <Check size={16} />
            {isRetryingContinuation || isRetryingDecision
              ? retryLabel ?? "Retry"
              : display.approveLabel}
          </Button>
          {display.canEdit && continuationStatus === undefined && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleEdit()}
              loading={pendingAction === "edited"}
              disabled={decisionsDisabled}
              data-testid="edit-btn"
              className="min-h-11 flex-1"
            >
              <Pencil size={16} />
              Edit
            </Button>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={() => void handleReject()}
            loading={pendingAction === "rejected"}
            disabled={decisionsDisabled || isRetryingContinuation || isRetryingDecision}
            data-testid="reject-btn"
            className="min-h-11 flex-1"
          >
            <X size={16} />
            Reject
          </Button>
        </div>
    </Card>
  );
}
